import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Container,
  Grid,
  Divider
} from '@mui/material';
import axios from 'axios';

interface RefereeInfo {
  name: string;
  email: string;
  relationship: string;
  jobTitle: string;
  company: string;
}

interface CounsellorInfo {
  firstName: string;
  lastName: string;
  email: string;
}

interface ReferenceFormData {
  howLongKnown: string;
  capacityKnown: string;
  suitability: string;
  concerns: string;
  additionalComments: string;
}

const ReferenceForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [expired, setExpired] = useState<boolean>(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState<boolean>(false);
  
  const [counsellor, setCounsellor] = useState<CounsellorInfo | null>(null);
  const [referee, setReferee] = useState<RefereeInfo | null>(null);
  
  const [formData, setFormData] = useState<ReferenceFormData>({
    howLongKnown: '',
    capacityKnown: '',
    suitability: '',
    concerns: '',
    additionalComments: ''
  });

  useEffect(() => {
    const validateToken = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/referee/reference/${token}`);
        
        setCounsellor(response.data.counsellor);
        setReferee(response.data.referee);
        
        // Check if reference has already been submitted
        if (response.data.referee.referenceSubmitted) {
          setAlreadySubmitted(true);
        }
        
        setLoading(false);
      } catch (err: any) {
        setLoading(false);
        if (err.response?.status === 404 || err.response?.status === 400) {
          setError('Invalid or expired reference token. Please contact the administrator.');
          setExpired(true);
        } else {
          setError('An error occurred while validating your reference token. Please try again later.');
        }
      }
    };
    
    validateToken();
  }, [token]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      await axios.post(`/api/referee/reference/${token}/submit`, formData);
      setSuccess(true);
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      setError('An error occurred while submitting your reference. Please try again later.');
    }
  };
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Alert severity="error">{error}</Alert>
        {expired && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body1">
              If you believe this is an error, please contact the administrator.
            </Typography>
          </Box>
        )}
      </Container>
    );
  }
  
  if (alreadySubmitted) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Alert severity="info">
          You have already submitted a reference for {counsellor?.firstName} {counsellor?.lastName}.
          Thank you for your contribution.
        </Alert>
      </Container>
    );
  }
  
  if (success) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Reference Submitted
          </Typography>
          <Alert severity="success" sx={{ mb: 4 }}>
            Your reference for {counsellor?.firstName} {counsellor?.lastName} has been submitted successfully.
          </Alert>
          <Typography variant="body1" paragraph>
            Thank you for taking the time to provide a reference. Your input is valuable to our selection process.
          </Typography>
          <Typography variant="body1">
            You may now close this window.
          </Typography>
        </Paper>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Reference Form
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Reference for {counsellor?.firstName} {counsellor?.lastName}
          </Typography>
          <Typography variant="body1" paragraph>
            {counsellor?.firstName} {counsellor?.lastName} has applied to join our counselling service and has provided your name as a referee.
            We would appreciate if you could complete this reference form to help us assess their suitability.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your information: {referee?.name} ({referee?.email})
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 4 }} />
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="How long have you known the applicant?"
                name="howLongKnown"
                value={formData.howLongKnown}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="In what capacity have you known the applicant?"
                name="capacityKnown"
                value={formData.capacityKnown}
                onChange={handleChange}
                variant="outlined"
                multiline
                rows={3}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl component="fieldset" required>
                <FormLabel component="legend">
                  Based on your knowledge of the applicant, do you believe they are suitable for a role as a counsellor?
                </FormLabel>
                <RadioGroup
                  name="suitability"
                  value={formData.suitability}
                  onChange={handleChange}
                >
                  <FormControlLabel value="Highly Suitable" control={<Radio />} label="Highly Suitable" />
                  <FormControlLabel value="Suitable" control={<Radio />} label="Suitable" />
                  <FormControlLabel value="Somewhat Suitable" control={<Radio />} label="Somewhat Suitable" />
                  <FormControlLabel value="Not Suitable" control={<Radio />} label="Not Suitable" />
                </RadioGroup>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Do you have any concerns about the applicant's suitability for this role?"
                name="concerns"
                value={formData.concerns}
                onChange={handleChange}
                variant="outlined"
                multiline
                rows={4}
                helperText="If you have no concerns, please write 'No concerns'"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional comments"
                name="additionalComments"
                value={formData.additionalComments}
                onChange={handleChange}
                variant="outlined"
                multiline
                rows={4}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                sx={{ minWidth: 200 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Submit Reference'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default ReferenceForm;
